import { useEffect, useRef, useState } from 'react';

import { c } from 'ttag';

import { Href } from '@proton/atoms/Href/Href';
import useApi from '@proton/components/hooks/useApi';
import { ping } from '@proton/shared/lib/api/tests';
import { HOUR, SECOND } from '@proton/shared/lib/constants';
import { captureMessage } from '@proton/shared/lib/helpers/sentry';
import { getKnowledgeBaseUrl } from '@proton/shared/lib/helpers/url';
import clsx from '@proton/utils/clsx';
import noop from '@proton/utils/noop';

import useApiServerTime from '../../hooks/useApiServerTime';
import TopBanner from './TopBanner';

const isOutOfSync = (serverTime: Date, localTime: Date) => {
    const timeDifference = Math.abs(serverTime.getTime() - localTime.getTime());
    // We should allow at least a 14-hour time difference,
    // because of potential internal clock issues when using dual-boot with Windows and a different OS
    return timeDifference > 24 * HOUR;
};

/**
 * The serverTime update might be stale if e.g. the device has been offline/asleep/idle, and it's processing the update after a delay.
 */
const isStaleServerTimeUpdate = (previousUpdateLocalTime: Date | undefined, localTime: Date) => {
    if (previousUpdateLocalTime === undefined) {
        return true;
    }
    const timeDifference = Math.abs(previousUpdateLocalTime.getTime() - localTime.getTime());
    // The event loop runs every 30s-120s (depending on the app), so we expect the server time to be updated at
    // least with that frequency (with a margin of a few seconds).
    // We stay on the upper end of the interval to avoid incorrectly flagging true positive out-of-sync times as
    // stale while waiting for the longer event loops to run (as this results in the banner being wrongly hidden
    // for a few seconds on re-renders, which can be triggered e.g. when navigating across Mail folders).
    // Note also that as long as the staleness threshold is below the out-of-sync one, staleness can only hide
    // a true positive banner, never trigger displaying a false positive one.
    // NB: we ignore exponential backoff interval on connectivity issues, which can delay API requests for up to
    // 300s, since the banner will always eventually be hidden on connectivity issues anyway (as the server time
    // will be marked as stale). This normally should not matter anyway as the offline banner should be shown
    // instead of this one (thanks to the "one-of" class logic), but this is not the case if the vpn is connected,
    // due to navigator.onLine limitations
    return timeDifference > 125 * SECOND;
};

type Props = { className?: string };

const TimeOutOfSyncTopBanner = ({ className }: Props) => {
    const [ignore, setIgnore] = useState(false);

    const api = useApi();
    const { serverTime, serverTimeUpdatedAt } = useApiServerTime() ?? {};
    const serverTimeUpdatedAtRef = useRef(serverTimeUpdatedAt);
    serverTimeUpdatedAtRef.current = serverTimeUpdatedAt;

    const currentUpdateLocalTime = new Date();
    const isStaleServerTime = isStaleServerTimeUpdate(serverTimeUpdatedAt, currentUpdateLocalTime);

    useEffect(() => {
        // Check for stale server time every 5s, and ping the server if needed to ensure that we retrieve an updated
        // server time at most 5s after waking from an idle state: we do not want to wait for the event loop
        // request to be processed, since the stale serverTime() value will be used by the apps in the meantime.
        const stalePingInterval = setInterval(() => {
            if (isStaleServerTimeUpdate(serverTimeUpdatedAtRef.current, new Date())) {
                void api({ ...ping() }).catch(noop); // if it fails, we'll try again in 5s
            }
        }, 5000);
        return () => {
            clearInterval(stalePingInterval);
        };
    }, []); // run only once, on first render

    // We warn the user if the server time is too far off from local time.
    // We do not want the server to set arbitrary times (either past or future), to avoid signature replay issues and more.
    const showWarning = !ignore && serverTime && !isStaleServerTime && isOutOfSync(serverTime, currentUpdateLocalTime);

    // Log warning to have an idea of how many clients might be affected
    const onceRef = useRef(false);
    useEffect(() => {
        if (!showWarning || onceRef.current) {
            return;
        }

        onceRef.current = true;
        captureMessage('Client time difference larger than 24 hours', {
            level: 'info',
            extra: {
                serverTime: serverTime?.toString(),
                localTime: currentUpdateLocalTime.toString(),
                isStaleServerTime,
            },
        });
    }, [showWarning]);

    if (!showWarning) {
        return null;
    }

    const learnMore = (
        <Href href={getKnowledgeBaseUrl('/device-time-warning')} key="device-time-link">{c('Link').t`Learn more`}</Href>
    );

    return (
        <TopBanner onClose={() => setIgnore(true)} className={clsx('bg-warning', className)}>
            {c('Warning').jt`The date and time settings on your device are out of sync. ${learnMore}`}
        </TopBanner>
    );
};

export default TimeOutOfSyncTopBanner;
